import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  FC,
  ReactNode,
} from 'react';

import {
  MessageContextType,
  MessageAction,
  TMessaging,
  TServiceTag,
  TConnection,
  TConversation,
  TConversationCategory,
  TConversationMessage,
  TMessageView,
  TUserConversation,
} from '../@types/message';
import { UserContext } from './userContext';

const iState: TMessaging = {
  setupComplete: false,
  activeView: 'list',
  isExpanded: false,
  activeConversation: undefined,
  conversations: undefined,
  connections: undefined,
  categories: [],
  tags: undefined,
  unseen: 0,
  pageSize: 10,
  starsOnTop: false,
  me: '',
};
export const MessagingContext = createContext<MessageContextType>({
  messageState: iState,
  dispatch: () => null,
  setActiveView: () => null,
  setActiveConversation: () => null,
  setCategories: () => null,
  setConversations: () => null,
  setUnseenMessageCount: () => null,
  setupMessaging: () => null,
  setPageSize: () => null,
  setStarsOnTop: () => null,
});

export const messageReducer = (
  messageState: TMessaging,
  action: MessageAction,
): TMessaging => {
  switch (action.type) {
    case 'SET_ACTIVE_VIEW':
      return {
        ...messageState,
        activeView: action.activeView,
      };
    case 'SET_ACTIVE_CONVERSATION':
      return {
        ...messageState,
        activeConversation: action.conversation,
      };
    case 'SET_CONVERSATION_CATEGORIES':
      return {
        ...messageState,
        categories: action.payload,
      };
    case 'SET_CONVERSATIONS':
      return {
        ...messageState,
        conversations: action.conversations,
      };
    case 'SET_UNSEEN_MESSAGE_COUNT':
      return {
        ...messageState,
        unseen: action.payload,
      };
    case 'SET_MY_USERNAME':
      return {
        ...messageState,
        me: action.payload,
      };
    case 'SET_SETUP_COMPLETE':
      return {
        ...messageState,
        setupComplete: true,
      };
    case 'SET_PAGE_SIZE':
      return {
        ...messageState,
        pageSize: action.payload,
      };
    case 'SET_STARS_ON_TOP':
      return {
        ...messageState,
        starsOnTop: action.payload,
      };
    case 'RESET_MESSAGING':
      return {
        ...iState,
      };
    default:
      return messageState;
  }
};

const MessagingProvider: FC<{
  children: ReactNode;
  onSetActiveView?: (payload: TMessaging['activeView']) => void;
  onSetActiveConversation?: (payload: TUserConversation | undefined) => void;
  onSetCategories?: (payload: TConversationCategory[]) => void;
  onSetConversations?: (payload: TUserConversation[]) => void;
  onSetUnseenMessageCount?: (payload: number) => void;
  onSetPageSize?: (payload: number) => void;
  onSetStarsOnTop?: (payload: boolean) => void;
  onSetupMessaging?: (
    username: TMessaging['me'],
    categories: TConversationCategory[],
  ) => void;
}> = ({
  children,
  onSetActiveView,
  onSetActiveConversation,
  onSetCategories,
  onSetConversations,
  onSetUnseenMessageCount,
  onSetPageSize,
  onSetStarsOnTop,
  onSetupMessaging,
}) => {
  const localState: TMessaging = iState;
  const initialState = useContext(MessagingContext);
  const [messageState, dispatch] = useReducer(
    messageReducer,
    localState || initialState,
  );

  // MessagingProvider is mounted once for the whole tab (gatsby-browser.tsx).
  // conversations/activeConversation hold actual private message content in
  // plain React state and are only repopulated lazily once the Messages UI
  // mounts (setupMessaging/setConversations). Reset on any false->true login
  // transition so a new user's session never renders the previous user's
  // conversations before their own data has loaded.
  const { state: userState } = useContext(UserContext);
  const prevIsLoggedIn = useRef(userState.isLoggedIn);
  useEffect(() => {
    if (!prevIsLoggedIn.current && userState.isLoggedIn) {
      dispatch({ type: 'RESET_MESSAGING' });
    }
    prevIsLoggedIn.current = userState.isLoggedIn;
  }, [userState.isLoggedIn]);

  const setActiveView = (activeView: TMessaging['activeView']) => {
    dispatch({ type: 'SET_ACTIVE_VIEW', activeView });
    onSetActiveView && onSetActiveView(activeView);
  };

  const setActiveConversation = (
    conversation: TUserConversation | undefined,
  ) => {
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', conversation });
    onSetActiveConversation && onSetActiveConversation(conversation);
  };

  const setCategories = (categories: TConversationCategory[]) => {
    dispatch({ type: 'SET_CONVERSATION_CATEGORIES', payload: categories });
    onSetCategories && onSetCategories(categories);
  };
  const setConversations = (conversations: TUserConversation[]) => {
    dispatch({ type: 'SET_CONVERSATIONS', conversations });
    onSetConversations && onSetConversations(conversations);
  };
  const setUnseenMessageCount = (count: number) => {
    dispatch({ type: 'SET_UNSEEN_MESSAGE_COUNT', payload: count });
    onSetUnseenMessageCount && onSetUnseenMessageCount(count);
  };
  const setPageSize = (pageSize: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize });
    onSetPageSize && onSetPageSize(pageSize);
  };
  const setStarsOnTop = (starsOnTop: boolean) => {
    dispatch({ type: 'SET_STARS_ON_TOP', payload: starsOnTop });
    onSetStarsOnTop && onSetStarsOnTop(starsOnTop);
  };
  const setupMessaging = (
    username: TMessaging['me'],
    categories: TConversationCategory[],
  ) => {
    dispatch({ type: 'SET_CONVERSATION_CATEGORIES', payload: categories });
    dispatch({ type: 'SET_MY_USERNAME', payload: username });
    dispatch({ type: 'SET_SETUP_COMPLETE' });
    onSetupMessaging && onSetupMessaging(username, categories);
  };

  return (
    <MessagingContext.Provider
      value={{
        messageState,
        setupMessaging,
        setActiveView,
        setActiveConversation,
        setCategories,
        setConversations,
        setUnseenMessageCount,
        setPageSize,
        setStarsOnTop,
        dispatch,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingProvider;
